import { type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import type { Agent } from '../agents/registry.ts';
import { withNext } from '../auth/postAuthRedirect.ts';
import './AgentNode.css';

interface AgentNodeProps {
  agent: Agent;
  /** Notify the parent that this agent is being hovered (sticky preview). */
  onMouseEnter: () => void;
  /** Click handler. The parent intercepts on mobile to preview-instead-of-navigate;
   *  on desktop it's a no-op and the underlying <Link> handles navigation. */
  onClick: (event: MouseEvent<HTMLAnchorElement>) => void;
}

/**
 * One agent button in the bottom rail. Click navigates straight to sign-up
 * (carrying `?next=` to the agent's console) on desktop. On mobile the parent's
 * onClick intercepts and shows the agent's meta block instead — the user then
 * taps the ENGAGE link to navigate.
 */
export default function AgentNode({ agent, onMouseEnter, onClick }: AgentNodeProps) {
  return (
    <Link
      to={withNext('/auth/signup', `/agent/${agent.slug}`)}
      data-agent={agent.slug}
      className={`om-agent-node om-agent-node--${agent.slug}`}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
    >
      <div className="om-agent-node__portrait">
        <div className="om-octagon">
          <div className="om-octagon__inner">
            <img src={agent.image} alt="" loading="eager" decoding="async" draggable={false} />
            <div className="om-octagon__vignette" />
            <div className="om-octagon__scanlines" />
          </div>
        </div>
      </div>
      <div className="om-agent-node__name">{agent.name}</div>
      <div className="om-agent-node__role">{agent.shortRole}</div>
      <div className={`om-agent-node__status om-agent-node__status--${agent.status.toLowerCase()}`}>
        <span className="om-agent-node__status-dot" /> {agent.status}
      </div>
    </Link>
  );
}
